import React from 'react';
import { Shield } from 'lucide-react';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen pt-20 bg-black">
      <SEO title="Privacy Policy | PopCrown Official" description="PopCrown's privacy policy. How we collect, use, and protect your data. GDPR compliant." keywords="PopCrown privacy policy, data protection, GDPR" canonical="/privacy" />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="blog-card p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <Shield className="w-12 h-12 text-[#E10600]" />
            <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
          </div>

          <p className="text-gray-400 mb-8">
            <strong>Effective Date:</strong> January 2026<br />
            <strong>Last Updated:</strong> January 2026
          </p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">1. Introduction</h2>
              <p className="mb-4">
                Welcome to PopCrown Official Ltd ("PopCrown", "we", "us", or "our"). We operate the PopCrown website (popcrownofficial.com), the PopCrown Social web application (members.popcrownofficial.com), and associated mobile applications.
              </p>
              <p className="mb-4">
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our web application, or download our mobile application. Please read this privacy policy carefully. By using our services, you consent to the practices described in this policy.
              </p>
              <p>
                Our services are available across multiple platforms including desktop web browsers, tablet devices, mobile web browsers, and native mobile applications (iOS and Android).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">2.1 Personal Information</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Name and display name</li>
                <li>Email address</li>
                <li>Profile photograph</li>
                <li>Date of birth</li>
                <li>Location (optional)</li>
                <li>Payment information (processed by third-party payment processors)</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, time spent, features used)</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Log data and analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and maintain our services</li>
                <li>To process your subscription and payments</li>
                <li>To send you newsletters and marketing communications (with your consent)</li>
                <li>To improve our website and app functionality</li>
                <li>To respond to your inquiries and provide customer support</li>
                <li>To detect and prevent fraud or abuse</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">4. GDPR Compliance (EU Users)</h2>
              <p className="mb-4">
                If you are located in the European Economic Area (EEA), you have certain data protection rights under GDPR:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right to Access:</strong> You can request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> You can request corrections to inaccurate data</li>
                <li><strong>Right to Erasure:</strong> You can request deletion of your data</li>
                <li><strong>Right to Restrict Processing:</strong> You can request limitations on data processing</li>
                <li><strong>Right to Data Portability:</strong> You can request transfer of your data</li>
                <li><strong>Right to Object:</strong> You can object to processing of your data</li>
                <li><strong>Right to Withdraw Consent:</strong> You can withdraw consent at any time</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">5. Marketing Communications</h2>
              <p className="mb-4">
                We will only send you marketing emails if you have explicitly consented to receive them. You can:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Unsubscribe at any time using the link in our emails</li>
                <li>Update your preferences in your account settings</li>
                <li>Contact us directly to opt-out</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">6. Data Sharing and Disclosure</h2>
              <p className="mb-4">We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Third parties who help us operate our services including:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Stripe (payment processing)</li>
                    <li>Brevo/Sendinblue (email communications)</li>
                    <li>Cloud hosting providers</li>
                    <li>Analytics services</li>
                  </ul>
                </li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">7. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal data, including encryption, secure servers, and regular security assessments. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">8. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. When you delete your account, we will permanently delete your data within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">9. Children's Privacy &amp; Age Restrictions</h2>
              <p className="mb-3">
                The free PopCrown app is available to users of all ages. We collect minimal data from free app users (see Section 2 above).
              </p>
              <p className="mb-3">
                <strong>The community features of this app — including the PopCrown Community feed, posts, comments, direct messages and any social interactions — are restricted to users aged 16 or over.</strong> We do not knowingly collect personal information from anyone under 16 in connection with our community features. This is in compliance with the UK Online Safety Act, UK GDPR, and our community safety standards.
              </p>
              <p>
                If you believe we have collected personal information from someone under 16 in our community features, please contact us immediately at <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a> and we will delete it without delay.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">10. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with GDPR and other applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a prominent notice on our website. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">12. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-[#1a1a1a] p-4 rounded-lg">
                <p><strong className="text-white">PopCrown Official Ltd</strong></p>
                <p>Email: <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;